import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import {
  Trophy, Building2, MapPin, Wind, Droplets, Tv, Calendar, Home as HomeIcon, ExternalLink,
} from "lucide-react";

const LEAGUE_ACCENT = {
  NFL:   "#d90429",   // GWN red
  MLB:   "#ffd60a",   // orange
  NBA:   "#a78bfa",   // violet
  MLS:   "#22d3ee",   // cyan
  NCAAF: "#ef4444",   // deep red
};

function formatKick(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return { day, time };
  } catch {
    return { day: iso, time: "" };
  }
}

function daysAway(iso) {
  if (!iso) return null;
  const diff = Math.floor((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return "In progress";
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff < 7) return `${diff} days`;
  return `${diff} days out`;
}

function GameCard({ game }) {
  const accent = LEAGUE_ACCENT[game.league] || "#ffd60a";
  const kick = formatKick(game.date);
  const wx = game.weather;
  const isAwayGame = !game.is_home;
  const inWindow = wx && !game.indoor;
  const beyondForecast = !inWindow && !game.indoor;

  return (
    <article
      data-testid={`gameday-card-${game.team_id}-${game.date}`}
      className="lift group relative overflow-hidden rounded-sm border border-white/10 bg-[#151b26]"
    >
      {/* Top accent bar */}
      <div className="h-1 w-full" style={{ backgroundColor: accent }} />

      <div className="p-5">
        {/* League + status pills */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className="rounded-full px-2 py-0.5 font-oswald text-[10px] font-bold uppercase tracking-widest text-black"
              style={{ backgroundColor: accent }}
            >
              {game.league}
            </span>
            <span className="font-oswald text-[10px] uppercase tracking-widest text-gray-400">
              {game.team_name}
            </span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
            {daysAway(game.date)}
          </span>
        </div>

        {/* Matchup */}
        <div className="mt-4 flex items-center gap-3">
          {game.opponent_logo && (
            <img src={game.opponent_logo} alt={game.opponent} className="h-12 w-12 object-contain" loading="lazy" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {game.is_home ? (
                <HomeIcon className="h-3 w-3 text-[#ffd60a]" />
              ) : (
                <ExternalLink className="h-3 w-3 text-gray-400" />
              )}
              <span className="font-oswald text-[10px] font-bold uppercase tracking-[0.3em] text-white">
                {game.is_home ? "vs" : "at"}
              </span>
            </div>
            <div className="mt-0.5 line-clamp-2 font-oswald text-lg font-bold uppercase leading-tight tracking-wider text-white">
              {game.opponent || "TBD"}
            </div>
          </div>
        </div>

        {/* Kickoff row */}
        <div className="mt-4 flex items-center gap-3 border-t border-white/5 pt-3">
          <Calendar className="h-3.5 w-3.5 text-[#ffd60a]" />
          <div className="font-oswald text-xs uppercase tracking-widest text-white">
            {kick.day} · <span className="text-[#ffd60a]">{kick.time}</span>
          </div>
        </div>

        {/* Venue */}
        <div className="mt-2 flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-gray-500" />
          <span className="truncate text-xs text-gray-400">
            {game.venue} · {game.venue_city}{game.venue_state ? `, ${game.venue_state}` : ""}
          </span>
        </div>

        {/* Broadcast */}
        {game.broadcast && (
          <div className="mt-1 flex items-center gap-2">
            <Tv className="h-3.5 w-3.5 text-gray-500" />
            <span className="truncate font-mono text-[10px] uppercase tracking-widest text-gray-400">
              {game.broadcast}
            </span>
          </div>
        )}

        {/* Weather panel */}
        <div className="mt-4 rounded-sm border border-white/10 bg-black/50 p-3">
          {isAwayGame && !inWindow && !game.indoor && game.weather_note ? (
            <div className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-gray-500" />
              <div>
                <div className="font-oswald text-xs font-bold uppercase tracking-widest text-white">Road Game · {game.venue_city}</div>
                <div className="text-[11px] text-gray-400">{game.weather_note}</div>
              </div>
            </div>
          ) : game.indoor ? (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#22d3ee]" />
              <div>
                <div className="font-oswald text-xs font-bold uppercase tracking-widest text-white">Indoor Venue</div>
                <div className="text-[11px] text-gray-400">Climate-controlled — no gameday weather impact</div>
              </div>
            </div>
          ) : inWindow ? (
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="font-oswald text-[10px] uppercase tracking-[0.3em] text-[#ffd60a]">
                  {isAwayGame ? `Road Game · ${game.venue_city}` : "Projected at Kickoff"}
                </div>
                <div className="font-oswald text-2xl font-bold text-white">
                  {wx.temperature}°<span className="ml-0.5 align-top text-[11px] text-gray-400">{wx.temperatureUnit}</span>
                </div>
              </div>
              <div className="mb-1 font-oswald text-xs uppercase tracking-wider text-white">
                {wx.shortForecast}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-300">
                <span className="flex items-center gap-1">
                  <Wind className="h-3 w-3 text-gray-500" /> {wx.windSpeed || "—"} {wx.windDirection || ""}
                </span>
                {wx.probabilityOfPrecipitation !== null && wx.probabilityOfPrecipitation !== undefined && (
                  <span className="flex items-center gap-1">
                    <Droplets className="h-3 w-3 text-gray-500" /> {wx.probabilityOfPrecipitation}% precip
                  </span>
                )}
                {wx.relativeHumidity !== null && wx.relativeHumidity !== undefined && (
                  <span className="text-gray-500">RH {wx.relativeHumidity}%</span>
                )}
              </div>
            </div>
          ) : beyondForecast ? (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <div>
                <div className="font-oswald text-xs font-bold uppercase tracking-widest text-white">Beyond 7-day{isAwayGame ? ` · ${game.venue_city}` : ""}</div>
                <div className="text-[11px] text-gray-400">Weather locks in as we get closer to kickoff</div>
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">Forecast unavailable</div>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Gameday() {
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activeTeam, setActiveTeam] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/gameday")
      .then((r) => { setGames(r.data.games || []); setTeams(r.data.teams || []); })
      .catch(() => setError("Couldn't load schedules"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (activeTeam === "all") return games;
    return games.filter((g) => g.team_id === activeTeam);
  }, [games, activeTeam]);

  return (
    <div className="relative min-h-screen">
      <Header />

      <section className="relative py-8 sm:py-10" data-testid="gameday-page">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          {/* Title */}
          <div className="mb-6 flex items-center gap-3">
            <Trophy className="h-6 w-6 text-[#ffd60a]" />
            <div>
              <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">
                Gameday Weather
              </div>
              <h1 className="mt-1 font-oswald text-3xl font-bold uppercase tracking-wider text-white sm:text-4xl lg:text-5xl">
                Peach State Gameday
              </h1>
            </div>
          </div>
          <p className="mb-6 max-w-3xl text-sm text-gray-400">
            Upcoming games for Georgia's major-league and Power-5 teams, matched with the NWS hourly forecast at each home stadium. Indoor venues carry a climate-controlled badge; road games show location only.
          </p>

          {/* Team filter tabs */}
          <div className="mb-6 flex flex-wrap gap-2" data-testid="gameday-team-tabs">
            <button
              onClick={() => setActiveTeam("all")}
              data-testid="gameday-team-all"
              className={`rounded-sm border px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest transition-colors ${
                activeTeam === "all"
                  ? "border-[#1f63c9] bg-[#1f63c9]/20 text-white"
                  : "border-white/10 bg-black/40 text-gray-300 hover:border-white/30 hover:text-white"
              }`}
            >
              All Teams
            </button>
            {teams.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTeam(t.id)}
                data-testid={`gameday-team-${t.id}`}
                className={`inline-flex items-center gap-2 rounded-sm border px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest transition-colors ${
                  activeTeam === t.id
                    ? "border-[#1f63c9] bg-[#1f63c9]/20 text-white"
                    : "border-white/10 bg-black/40 text-gray-300 hover:border-white/30 hover:text-white"
                }`}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: LEAGUE_ACCENT[t.league] || "#ffd60a" }}
                />
                {t.name.replace(/^Atlanta /, "").replace(/^Georgia /, "")}
                <span className="font-mono text-[9px] text-gray-400">· {t.league}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-64 animate-pulse rounded-sm border border-white/10 bg-white/[0.02]" />
              ))}
            </div>
          ) : error ? (
            <div className="rounded border border-dashed border-white/15 p-14 text-center">
              <div className="font-oswald text-lg uppercase tracking-widest text-white">{error}</div>
              <p className="mt-1 text-sm text-gray-400">Refresh in a minute — schedule and forecast providers occasionally rate-limit.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded border border-dashed border-white/15 p-14 text-center">
              <Trophy className="mx-auto h-10 w-10 text-gray-500" />
              <div className="mt-3 font-oswald text-lg uppercase tracking-widest text-white">Off-Season</div>
              <p className="mt-1 text-sm text-gray-400">No games on the calendar for this team right now.</p>
            </div>
          ) : (
            <>
              <div className="mb-3 font-mono text-[11px] uppercase tracking-widest text-gray-500">
                {filtered.length} game{filtered.length === 1 ? "" : "s"} upcoming
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((g) => (
                  <GameCard key={`${g.team_id}-${g.date}-${g.opponent}`} game={g} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
